import React from 'react';

export function GoalCard({ goal, selected, onSelect, subtitle }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(goal)}
      className={`w-full text-left rounded border p-3 ${selected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">{goal.code || 'Mål'}</div>
        {goal.status ? <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{goal.status}</span> : null}
      </div>
      <div className="text-sm text-slate-700">{goal.title}</div>
      {subtitle ? <div className="text-xs text-slate-600 mt-1">{subtitle}</div> : null}
    </button>
  );
}

export function StatusDot({ color }) {
  const c = String(color || '').toLowerCase();
  const cls =
    c === 'red'
      ? 'bg-red-500'
      : c === 'yellow'
        ? 'bg-amber-400'
        : c === 'green'
          ? 'bg-emerald-500'
          : 'bg-slate-300';
  const label = c ? c.toUpperCase() : '—';
  return (
    <span className="inline-flex items-center gap-2" title={label}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${cls}`} />
    </span>
  );
}

export function KpiRow({ kpi, selected, onSelect, showStatusDot = true }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(kpi)}
      className={`w-full text-left rounded border p-3 ${selected ? 'border-slate-900 bg-slate-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold">{kpi.name}</div>
        <div className="flex items-center gap-2">
          {kpi.status ? <span className="text-[11px] px-2 py-0.5 rounded bg-slate-100 text-slate-700">{kpi.status}</span> : null}
          {showStatusDot ? <StatusDot color={kpi.status_color} /> : null}
        </div>
      </div>
      <div className="text-xs text-slate-600">
        {kpi.unit ? `Enhet: ${kpi.unit}` : 'Enhet: -'}
        {kpi.frequency ? ` • Frekvens: ${kpi.frequency}` : ''}
        {kpi.target_value !== null && kpi.target_value !== undefined ? ` • Mål: ${kpi.target_value}` : ''}
        {kpi.latest_value !== null && kpi.latest_value !== undefined ? ` • Senast: ${kpi.latest_value}` : ''}
        {kpi.yellow_threshold !== null && kpi.yellow_threshold !== undefined ? ` • Gul: ${kpi.yellow_threshold}` : ''}
        {kpi.red_threshold !== null && kpi.red_threshold !== undefined ? ` • Röd: ${kpi.red_threshold}` : ''}
      </div>
    </button>
  );
}

export function TextField({ label, value, onChange, placeholder, className = '', inputClassName = '' }) {
  return (
    <label className={`block ${className}`}>
      {label ? <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div> : null}
      <input
        className={`w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm ${inputClassName}`}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function TextArea({ label, value, onChange, placeholder, rows = 4, className = '' }) {
  return (
    <label className={`block ${className}`}>
      {label ? <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div> : null}
      <textarea
        className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
        value={value}
        placeholder={placeholder}
        rows={rows}
        onChange={(e) => onChange(e.target.value)}
      />
    </label>
  );
}

export function SelectField({ label, value, onChange, options, className = '' }) {
  return (
    <label className={`block ${className}`}>
      {label ? <div className="text-xs font-semibold text-slate-700 mb-1">{label}</div> : null}
      <select
        className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-sm"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

export function Button({ children, variant = 'primary', onClick, disabled, type = 'button', className = '' }) {
  const base = 'inline-flex items-center justify-center rounded px-3 py-1.5 text-sm font-semibold border';
  const styles =
    variant === 'primary'
      ? 'bg-slate-900 text-white border-slate-900 hover:bg-slate-800'
      : variant === 'danger'
        ? 'bg-red-600 text-white border-red-600 hover:bg-red-500'
        : variant === 'ghost'
          ? 'bg-white text-slate-800 border-slate-300 hover:bg-slate-50'
          : 'bg-slate-100 text-slate-800 border-slate-200 hover:bg-slate-200';
  return (
    <button type={type} onClick={onClick} disabled={disabled} className={`${base} ${styles} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      {children}
    </button>
  );
}
